/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FunctionComponent} from 'react';
import {AssetType} from '@ion/core/src/types/entities';
import {dlsColor} from '../theme/theme';
import {Color, IconProps, IconSize} from '../types/types';
import {AssetCargoIcon} from './AssetCargoIcon';
import {AssetEquipmentIcon} from './AssetEquipmentIcon';
import {AssetToolIcon} from './AssetToolIcon';
import {AssetTrailerIcon} from './AssetTrailerIcon';
import {AssetVehicleIcon} from './AssetVehicleIcon';

export interface AssetTypeIconProps {
  assetType: string;
  darkMode?: boolean;
  color?: Color;
  stroke?: string;
  size?: IconSize;
}

/**
 * Provides a the mappings between the type prop (Asset.type) and the Asset's
 * respective icon.
 */
const assetTypeIconMap = {
  [AssetType.Vehicle]   : AssetVehicleIcon,
  [AssetType.Machine]   : AssetCargoIcon,
  [AssetType.Powered]   : AssetCargoIcon,
  [AssetType.Unpowered] : AssetTrailerIcon,
  [AssetType.Equipment] : AssetEquipmentIcon,
  [AssetType.Tool]      : AssetToolIcon,
  [AssetType.Default]   : AssetVehicleIcon,
  [AssetType.Trailer]   : AssetTrailerIcon
};

/**
 * @AssetTypeIcon
 *
 * The main purpose of the AssetTypeIcon is to provide a map from the asset
 * type that we may receive from the CVS (received as a prop here) and map it
 * to the appropriate svg Asset Icon component.
 *
 * The fill color of the svg will change based on the darkMode prop
 *
 * ```typescript
 *     <AssetTypeIcon assetType={AssetType.VEHICLE} darkMode />
 *
 * ```
 */
export const AssetTypeIcon = ({
  assetType,
  color,
  darkMode = false,
  ...props
}: AssetTypeIconProps) => {
  const AssetIcon: FunctionComponent<IconProps> = assetType?.toLowerCase() === 'trailer'
    ? AssetTrailerIcon : assetTypeIconMap[assetType] || AssetVehicleIcon;
  return (
    <AssetIcon color={color || (darkMode ? dlsColor('white') : dlsColor('grey4'))} {...props} />
  );
};

export default AssetTypeIcon;
